/**
 * 오피스 편집기 AI — 한 턴의 전송 클라이언트.
 *
 * 편집기(`packages/office`) 안이 아니라 **호스트**에 있다. 이유 셋:
 *   ① `packages/office/src/host.ts` 가 명시한 설계 그대로다 — "편집기는 우리 앱의 axios·
 *      스토어를 직접 import 하지 않는다. 호스트가 필요한 것만 함수로 넘긴다."
 *   ② 편집기 번들이 **다섯 벌**이라(md/docx/pdf/xlsx/pptx) 안에 두면 같은 클라이언트가
 *      다섯 번 실려 서로 어긋난다.
 *   ③ 한글(HWP)·평문 편집기용 패널도 **같은 클라이언트**를 써야 답변 품질이 편집기마다
 *      갈리지 않는다.
 *
 * ⚠ `apiClient`(axios)는 못 쓴다 — `timeout: 30_000` 이라 한 턴을 못 태우고 스트림도 못 읽는다.
 * ⚠ `EventSource` 는 레포 금지(쿠키·헤더 제어 불가). `fetch` + `credentials:'include'` 로 읽는다.
 */

/** 서버가 흘려보내는 청크 — `packages/office` 의 `IpcStreamChunk` 와 **구조가 같아야 한다**. */
interface TurnChunk {
  requestId: string;
  type: 'ping' | 'delta' | 'tool-call' | 'done' | 'error';
  text?: string;
  toolCall?: { id: string; name: string; input: Record<string, unknown>; inputError?: string };
  error?: string;
  errorCode?: 'timeout' | 'credits';
  stopReason?: string;
}

export interface OfficeAiToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  inputError?: string;
}

export interface OfficeAiTurnCallbacks {
  onPing(): void;
  onDelta(text: string): void;
  onToolCall(call: OfficeAiToolCall): void;
  onDone(stopReason?: string): void;
  onError(message: string, code?: 'timeout' | 'credits'): void;
}

export interface OfficeAiTurnRequest {
  requestId: string;
  runId: string;
  editorKind: 'markdown' | 'docs' | 'pdf' | 'sheets' | 'slides' | 'hwp' | 'text';
  fileId?: string;
  system: string;
  messages: unknown[];
  tools: unknown[];
}

const ENDPOINT = '/api/v1/office/ai/turn';

/** requestId → 진행 중 요청. 취소·정리에 쓴다. */
const inflight = new Map<string, AbortController>();

/** 취소로 끝난 요청 — 종단 보증에서 `done` 과 `error` 를 가른다. */
const cancelled = new Set<string>();

const FALLBACK_ERROR = 'AI 응답이 중단되었습니다.';

/**
 * 한 턴을 시작한다.
 *
 * ⚠ **스트림이 열리면 resolve, 거절되면 reject** 해야 한다. 편집기 쪽 `createIpcTransport` 가
 *   reject 를 `onError` 로 바꿔 실행을 끝내는데, 여기서 조용히 resolve 해 버리면 렌더러가
 *   240초 워치독이 돌 때까지 "생각 중" 으로 멈춰 있는다.
 */
export async function startOfficeAiTurn(
  req: OfficeAiTurnRequest,
  cb: OfficeAiTurnCallbacks,
): Promise<void> {
  const ac = new AbortController();
  inflight.set(req.requestId, ac);
  cancelled.delete(req.requestId);

  let res: Response;
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      // ⚠ 이미지는 보내지 않는다 — 비전 경로가 없고 bodyLimit 만 잡아먹는다.
      //   서버도 한 번 더 떼지만(이중 방어) 대역폭은 여기서 아낀다.
      body: JSON.stringify({ ...req, messages: stripImages(req.messages) }),
      signal: ac.signal,
    });
  } catch (err) {
    inflight.delete(req.requestId);
    if (ac.signal.aborted) {
      // 시작도 전에 취소 — 계약상 done 은 내야 한다.
      cb.onDone();
      return;
    }
    throw new Error((err as Error)?.message ?? FALLBACK_ERROR);
  }

  if (!res.ok || !res.body) {
    inflight.delete(req.requestId);
    const detail = await res.json().catch(() => ({}) as Record<string, unknown>);
    throw new Error(
      typeof detail.message === 'string' ? detail.message : `AI 요청이 거절되었습니다 (HTTP ${res.status}).`,
    );
  }

  // 헤더가 왔다 = 접수됐다. 본문 읽기는 떼어 놓고 즉시 resolve 한다.
  void pump(res.body, req.requestId, cb, ac.signal).finally(() => inflight.delete(req.requestId));
}

/**
 * 진행 중인 턴을 취소한다.
 *
 * ⚠ **멱등이어야 한다** — 편집기 쪽에서 침묵 워치독과 사용자의 중지 버튼이 각각 부른다.
 */
export function cancelOfficeAiTurn(requestId: string): void {
  const ac = inflight.get(requestId);
  if (!ac) return;
  cancelled.add(requestId);
  inflight.delete(requestId);
  ac.abort();
}

async function pump(
  body: ReadableStream<Uint8Array>,
  requestId: string,
  cb: OfficeAiTurnCallbacks,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  let settled = false;

  // ⚠ 취소했을 때 **본문 스트림이 알아서 끊긴다고 믿지 않는다.** 보통은 fetch 의 abort 가
  //   본문까지 오류로 만들지만, 그 전파는 환경에 따라 다르다. 안 끊기면 `read()` 가 영영
  //   대기해 아래 종단 보증이 못 돌고, 편집기는 워치독(240초)까지 멈춘 것처럼 보인다.
  //   → 리더를 직접 닫아 확실히 루프를 끝낸다.
  const onAbort = (): void => void reader.cancel().catch(() => {});
  signal.addEventListener('abort', onAbort);

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const chunk = parseChunk(block);
        if (!chunk || chunk.requestId !== requestId || settled) continue;
        if (chunk.type === 'ping') cb.onPing();
        else if (chunk.type === 'delta') cb.onDelta(chunk.text ?? '');
        else if (chunk.type === 'tool-call') {
          if (chunk.toolCall) cb.onToolCall(chunk.toolCall);
        } else if (chunk.type === 'done') {
          settled = true;
          cb.onDone(chunk.stopReason);
        } else {
          settled = true;
          cb.onError(chunk.error ?? FALLBACK_ERROR, chunk.errorCode);
        }
      }
    }
  } catch {
    /* 아래 종단 보증이 처리한다 */
  } finally {
    signal.removeEventListener('abort', onAbort);
    void reader.cancel().catch(() => {});
  }

  // ── 종단 보증 ─────────────────────────────────────────────────────────
  // 종단 프레임 없이 소켓이 끊기는 경로가 실재한다(프록시 절단·서버 재시작). 아무것도 안
  // 내면 편집기는 240초 워치독까지 "생각 중" 으로 멈춰 있다.
  if (!settled) {
    // 취소는 실패가 아니다 — 계약상 `cancel()` 뒤에도 반드시 `done` 이 와야 한다
    // (`agent-core/src/types.ts` 의 AgentStreamHandle 주석).
    if (cancelled.has(requestId)) cb.onDone();
    else cb.onError(FALLBACK_ERROR);
  }
  cancelled.delete(requestId);
}

function parseChunk(block: string): TurnChunk | null {
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith(':')) continue; // 코멘트(keepalive)
    if (!line.startsWith('data:')) continue;
    const v = line.slice(5);
    data.push(v.startsWith(' ') ? v.slice(1) : v);
  }
  if (!data.length) return null;
  try {
    return JSON.parse(data.join('\n')) as TurnChunk;
  } catch {
    return null;
  }
}

/** 사용자 메시지의 이미지를 뗀다 — 서버가 어차피 텍스트로 대체 안내를 넣는다. */
function stripImages(messages: unknown[]): unknown[] {
  return messages.map((m) => {
    const msg = m as { role?: string; images?: unknown[] };
    if (msg?.role === 'user' && Array.isArray(msg.images) && msg.images.length) {
      return { ...msg, images: msg.images.map(() => ({ base64: '', mime: '' })) };
    }
    return m;
  });
}
